import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, Users } from "lucide-react";
import AuthShell from "../components/AuthShell";
import KodPole, { PoslatZnovu } from "../components/KodPole";
import { Button, Field, Spinner } from "../components/ui/kit";
import api, { apiError } from "../lib/api";
import { ROLE_LABEL } from "../lib/nav";
import { ZnakFirmy } from "./Login";

/**
 * Přijetí pozvánky (deska 19): karta firmy a role → „Přijmout“ → kód z e-mailu
 * → jsi uvnitř s ověřeným e-mailem. Kdo už účet jinde má, žádné jméno ani
 * heslo nevyplňuje — firma se mu jen přidá do přepínače.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(undefined);
  const [krok, setKrok] = useState("pozvanka");     // pozvanka | kod | hotovo
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [kodChyba, setKodChyba] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vysledek, setVysledek] = useState(null);

  useEffect(() => {
    api.get(`/api/team/invite/${encodeURIComponent(token)}`)
      .then(({ data }) => { setInfo(data); setName(data.name || ""); })
      .catch(() => setInfo(null));
  }, [token]);

  const chyba = (err) => setError(apiError(err.response?.data?.detail) || err.message);
  const poslatKod = async () => { await api.post(`/api/team/invite/${encodeURIComponent(token)}/kod`); };

  const prijmout = async (e) => {
    e.preventDefault();
    if (!info.ma_ucet && name.trim().length < 2) return setError("Napiš svoje jméno.");
    setError(""); setLoading(true);
    try { await poslatKod(); setKrok("kod"); } catch (err) { chyba(err); } finally { setLoading(false); }
  };

  const overit = async (kod) => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/api/team/invite/accept", { token, kod, name: info.ma_ucet ? "" : name });
      setVysledek(data); setKrok("hotovo");
    } catch (err) { chyba(err); setKodChyba((x) => x + 1); } finally { setLoading(false); }
  };

  if (info === undefined) {
    return <AuthShell title="Pozvánka"><div className="flex items-center gap-2 text-muted"><Spinner /> Načítám…</div></AuthShell>;
  }
  if (info === null) {
    return (
      <AuthShell title="Neplatná pozvánka" footer={<Link to="/login" className="text-accent font-600">Na přihlášení</Link>}>
        <div className="card p-5 t-telo text-sec" data-testid="invite-invalid">
          Tahle pozvánka už neplatí — vypršela, byla použitá, nebo poslali novější. Požádej o novou.
        </div>
      </AuthShell>
    );
  }

  const role = (info.roles || []).map((r) => ROLE_LABEL[r] || r).join(", ");

  if (krok === "hotovo") {
    return (
      <AuthShell title={`Vítej v ${info.company_name}`}
        subtitle={info.ma_ucet ? "Firma se ti přidala k ostatním. Přepínáš v menu pod svou fotkou."
          : "E-mail máš ověřený. Příště se přihlásíš kódem z e-mailu."}>
        <div className="space-y-5" data-testid="invite-done">
          <span className="h-14 w-14 rounded-full bg-success text-white inline-flex items-center justify-center -mt-2"><Check size={28} strokeWidth={3} /></span>
          {info.ma_ucet && vysledek?.user && (
            <p className="t-meta text-muted">Teď jsi přihlášený do {info.company_name} jako {role.toLowerCase()}.</p>
          )}
          <Button className="w-full justify-center tap" onClick={() => window.location.assign("/")} data-testid="invite-pokracovat">
            Pokračovat do aplikace
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (krok === "kod") {
    return (
      <AuthShell title="Zadej kód z e-mailu"
        subtitle={<>Poslali jsme ho na <b className="text-ink font-700">{info.email}</b>. Platí 5 minut.</>}>
        <div className="space-y-5">
          <button type="button" onClick={() => { setKrok("pozvanka"); setError(""); }} aria-label="Zpět"
            className="tap -mt-3 h-10 w-10 rounded-full bg-white shadow-[inset_0_0_0_1px_#e6e5e1] inline-flex items-center justify-center text-ink">
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>
          <KodPole key={kodChyba} onHotovo={overit} disabled={loading} chyba={kodChyba > 0 && !!error} testid="invite-kod" />
          {loading && <div className="flex items-center gap-2 t-meta text-muted"><Spinner /> Ověřuji…</div>}
          {error && <div className="t-meta text-danger bg-[#fdecea] rounded-input px-3 py-2">{error}</div>}
          <PoslatZnovu onPoslat={async () => { setError(""); try { await poslatKod(); } catch (err) { chyba(err); } }} />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Pozvánka do týmu"
      subtitle={<>Pozvánka pro <b className="text-ink font-700">{info.email}</b></>}>
      <form onSubmit={prijmout} className="space-y-4" data-testid="invite-form">
        <div className="rounded-[20px] bg-ink text-white p-[18px] space-y-3.5">
          <div className="flex items-center gap-3">
            <ZnakFirmy nazev={info.company_name} logo={info.company_logo} velikost={48} />
            <span className="min-w-0">
              <span className="block label-up text-ondark-muted">Pozvánka do firmy</span>
              <span className="block text-[18px] font-800 truncate">{info.company_name}</span>
            </span>
          </div>
          <p className="t-telo md:text-[14px] text-[#c9ccd1] leading-relaxed">
            {info.pozval || "Majitel firmy"} tě přidává jako <b className="text-white">{role}</b>.
          </p>
        </div>
        {info.ma_ucet ? (
          <div className="flex gap-2.5 items-start rounded-[14px] bg-[#eef4ff] text-[#2f5fb3] px-3.5 py-3 t-meta md:text-[13.5px] leading-relaxed" data-testid="invite-ma-ucet">
            <Users size={17} strokeWidth={2} className="shrink-0 mt-0.5" />
            <span>Tenhle e-mail už v ProfitPilotu máš. Firmu ti přidáme k ostatním — <b>žádné nové heslo</b>.</span>
          </div>
        ) : (
          <Field label="Tvoje jméno">
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" data-testid="invite-name" />
          </Field>
        )}
        {error && <div className="t-meta text-danger bg-[#fdecea] rounded-input px-3 py-2">{error}</div>}
        <Button className="w-full justify-center tap" disabled={loading} data-testid="invite-accept">
          {loading ? <Spinner /> : "Přijmout pozvánku"}
        </Button>
        <p className="t-meta text-muted text-center">Pro jistotu ti pošleme kód na e-mail.</p>
      </form>
    </AuthShell>
  );
}
